import {RenderingPrioritizerBoundary} from '@aboutyou/data-fetcher';
import {useTranslation} from '@aboutyou/i18n';
import {
  ANCHOR_OFFSET_FROM_STICKY_HEADER,
  InteractiveProductTile,
  NavigationTreeLoading,
  ProductMiniAdpWithBasketFlyout,
  PromotionBanner,
  STREAM_TOP_SPACING,
  SizeSelectionOpenedStateKey,
  WishlistHeader,
  WishlistHeaderBanner,
  WishlistItemStream,
  WishlistItemStreamLoading,
  WishlistNavigationTree,
  WishlistPageMeta,
  WishlistSizeReminder,
  useAutoAddToWishlist,
  useIsNewBannerFrameworkActive,
  useMiniAdpDialogOpenedState,
  useSizeReminderPopupOpenedState,
} from '@aboutyou/logic-components';
import {ScrollTopAnchor} from '@aboutyou/router';
import {useTrackFeature} from '@aboutyou/tracking';
import {
  AddToBasketCtaContainer,
  ModalDialog,
  RowSection,
  SPACING,
  SplitType,
  SplitView,
  Z_INDEXES,
  gridColsWidth,
} from '@aboutyou/ui-components';
import {useClearWishlistBubble, useWishlistCount} from '@aboutyou/user';
import {styled} from '@linaria/react';
import React, {useCallback, useEffect, useRef} from 'react';
import {Sticky} from 'react-stickup-ay';

const splitViewConfig: {
  split: SplitType;
  splitMedium: SplitType;
  splitLarge: SplitType;
} = {
  split: '30:70',
  splitMedium: '20:80',
  splitLarge: '15:85',
};

// Show button for any touch device which does not support hover
const WishlistProductTile = styled(InteractiveProductTile)`
  @media (hover: none) {
    ${AddToBasketCtaContainer} {
      opacity: 1;
      grid-template-rows: 1fr;
      transform: translateY(0);
    }
  }
`;

export const WishlistPageEager = () => {
  const {t} = useTranslation();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const topanchorRef = useRef<HTMLDivElement>(null);
  const clearWishlistBubbleCallback = useClearWishlistBubble();

  const trackFeature = useTrackFeature();
  const {open: openMiniAdp} = useMiniAdpDialogOpenedState();

  const {open: openSizeReminder, parameter: sizeReminderParam} =
    useSizeReminderPopupOpenedState();

  useEffect(() => {
    clearWishlistBubbleCallback();
  }, [clearWishlistBubbleCallback]);

  const handleOnClickAddToBasket = useCallback(
    ({
      productId,
      couponPromotionId,
    }: {
      productId: number;
      couponPromotionId?: string;
    }) => {
      trackFeature({eventType: 'click', label: 'WL_A2B'});
      openMiniAdp({productId, couponPromotionId, locationId: 'product_stream'});
    },
    [],
  );

  const handleOnClickSoldoutCta = useCallback((productId: number) => {
    openSizeReminder({parameter: `${productId}`});
  }, []);

  const {count: wishlistCount} = useWishlistCount();
  const isWishlistEmpty = wishlistCount === 0;

  const activateNewBannerFramework = useIsNewBannerFrameworkActive();

  const {isAutoAddToWishlistLoading} = useAutoAddToWishlist();

  if (isAutoAddToWishlistLoading) {
    return (
      <RowSection>
        <StyledSection>
          <WishlistPageMeta />
          <SplitView
            {...splitViewConfig}
            contentStart={!isWishlistEmpty && <StyledNavigationTreeLoading />}
            contentEnd={
              <StyledSection>
                <WishlistItemStreamLoading />
              </StyledSection>
            }
          />
        </StyledSection>
      </RowSection>
    );
  }

  return (
    <RowSection>
      <WishlistPageMeta />
      <ScrollTopAnchor
        defaultOffsetTop={ANCHOR_OFFSET_FROM_STICKY_HEADER}
        anchorRef={topanchorRef}
        as={SplitView}
        {...splitViewConfig}
        disableSplit={isWishlistEmpty}
        refStart={sidebarRef}
        contentStart={
          !isWishlistEmpty && (
            <SidebarNavigationAside aria-label={t('Wishlist navigation')}>
              <Sticky
                overflowScroll="flow"
                experimentalNative
                container={sidebarRef}
                style={{zIndex: Z_INDEXES.CATEGORY_TREE}}
                defaultOffsetTop={ANCHOR_OFFSET_FROM_STICKY_HEADER}
              >
                <StyledWishlistNavigationTree excludeUrlParams={['sort']} />
              </Sticky>
            </SidebarNavigationAside>
          )
        }
        contentEnd={
          <StyledSection>
            <RenderingPrioritizerBoundary>
              {!activateNewBannerFramework && (
                <WishlistHeaderBanner renderingPriority={1} />
              )}
              {!isWishlistEmpty && <WishlistHeader filterVariant="dropdown" />}
              {activateNewBannerFramework && (
                <PromotionBannerWrapper
                  data-constrain-width={isWishlistEmpty ? 'true' : 'false'}
                >
                  <PromotionBanner
                    data-testid="wishlistPromotionBanner"
                    ContentPopperLayoutComponent={ModalDialog}
                    renderingPriority={1}
                    showSliderNavigation
                  />
                </PromotionBannerWrapper>
              )}
              <WishlistItemStream
                onClickAddToBasket={handleOnClickAddToBasket}
                onClickSoldoutCta={handleOnClickSoldoutCta}
                ProductTileComponent={WishlistProductTile}
                renderingPriority={2}
              />
            </RenderingPrioritizerBoundary>

            <ProductMiniAdpWithBasketFlyout
              sizeSelectionStateManagerKey={
                SizeSelectionOpenedStateKey.MAIN_SIZE_SELECTION
              }
            />
          </StyledSection>
        }
      />
      <WishlistSizeReminder productId={Number(sizeReminderParam ?? '')} />
    </RowSection>
  );
};

const PromotionBannerWrapper = styled.div`
  margin-bottom: ${SPACING['Y.Xl2']};

  &[data-constrain-width='true'] {
    margin-inline: auto;
    max-width: ${gridColsWidth(8)};
  }

  &:empty {
    margin-bottom: ${SPACING['Y.None']};
  }
`;

const StyledSection = styled.section`
  padding-top: ${STREAM_TOP_SPACING}px;
  padding-bottom: 1rem;
`;

// `<aside>` is needed for the landmark a11y rules, but it must not introduce
// a CSS box: Sticky's `container={sidebarRef}` positioning depends on being
// nested exactly as it was before this wrapper was added.
const SidebarNavigationAside = styled.aside`
  display: contents;
`;

const StyledWishlistNavigationTree = styled(WishlistNavigationTree)`
  padding-top: ${STREAM_TOP_SPACING}px;
  word-break: break-word;
`;

const StyledNavigationTreeLoading = styled(NavigationTreeLoading)`
  padding-top: ${STREAM_TOP_SPACING}px;
  word-break: break-word;
`;
